import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function checks(pw) {
  return {
    len: pw.length >= 8,
    letter: /[A-Za-z]/.test(pw),
    digit: /\d/.test(pw),
  };
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const c = useMemo(() => checks(pw), [pw]);
  const allValid = c.len && c.letter && c.digit && pw === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Missing reset token — request a new link"); return; }
    if (!allValid) { toast.error("Please meet all password rules first"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Password reset — please sign in with your new password");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-12 relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-[0.08]" />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
            <img src="/rkf-logo.webp" alt="RKF" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display font-bold text-lg">My RKF</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest">Set a new password</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight">
            One-time link<br/>one strong password<span className="text-blue-400">.</span>
          </h1>
          <p className="mt-4 text-slate-400 max-w-md text-sm leading-relaxed">
            Once you save, every other session is signed out. You'll use the new password from now on.
          </p>
        </div>
        <div className="relative text-xs text-slate-500 font-mono">v1.0 · Secure by design</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="p-8" data-testid="reset-password-card">
            {!token ? (
              <div className="text-center space-y-3" data-testid="reset-password-missing-token">
                <p className="text-red-700 font-medium">This link is missing its token.</p>
                <p className="text-sm text-slate-600">
                  Please request a new one from the sign-in page.
                </p>
                <Link to="/forgot-password" className="inline-block text-sm text-blue-600 hover:underline">
                  Request a new link
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <KeyRound className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-bold">New password</h2>
                    <p className="text-sm text-slate-500 mt-1">
                      Choose something strong — you'll use it every day.
                    </p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="new-pw">New password</Label>
                  <div className="relative">
                    <Input id="new-pw" type={showPw ? "text" : "password"}
                           value={pw} onChange={(e) => setPw(e.target.value)}
                           required autoFocus data-testid="reset-password-new" />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                            className="absolute right-2 top-2 text-slate-400 hover:text-slate-700"
                            aria-label={showPw ? "Hide" : "Show"}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm-pw">Confirm password</Label>
                  <Input id="confirm-pw" type={showPw ? "text" : "password"}
                         value={confirm} onChange={(e) => setConfirm(e.target.value)}
                         required data-testid="reset-password-confirm" />
                </div>

                <ul className="text-xs space-y-1" data-testid="reset-password-rules">
                  <Rule ok={c.len}>At least 8 characters</Rule>
                  <Rule ok={c.letter}>At least one letter</Rule>
                  <Rule ok={c.digit}>At least one number</Rule>
                  <Rule ok={pw && pw === confirm}>Both entries match</Rule>
                </ul>

                <Button type="submit" disabled={loading || !allValid}
                        className="w-full bg-[#0F172A] hover:bg-slate-800"
                        data-testid="reset-password-submit">
                  {loading ? "Saving..." : "Set new password"}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  <Link to="/login" className="text-slate-500 hover:text-slate-700">Back to sign in</Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Rule({ ok, children }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-slate-500"}`}>
      <span className={`w-4 h-4 rounded-full border flex items-center justify-center text-[10px] ${ok ? "border-emerald-500 bg-emerald-100" : "border-slate-300"}`}>
        {ok ? "✓" : ""}
      </span>
      {children}
    </li>
  );
}
